import {testIdProps} from '@github-ui/test-id-props'
import {NoteIcon, PencilIcon} from '@primer/octicons-react'
import {Button, FormControl, IconButton, Textarea} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import {EmojiAutocomplete} from '../components/common/emoji-autocomplete'
import {ViewerPrivileges} from '../helpers/viewer-privileges'
import {useProjectDetails} from '../state-providers/memex/use-project-details'
import {useUpdateMemexShortDescription} from '../state-providers/memex/use-update-memex-short-description'
import {DescriptionEditorResources} from '../strings'
import {ErrorState, SuccessState} from './common/state-style-decorators'
import {SanitizedHtml} from './dom/sanitized-html'
import styles from './short-description-editor.module.css'

const MAX_LENGTH = 300
const TIMEOUT_SUCCESS_HIDE = 1800

interface ShortDescriptionEditorProps {
  setProjectShortDescription?: (shortDescription: string) => void
  onSaveStats?: (update: boolean) => void
  /**
   * Control whether this renders as a preview with an edit button (`false`) or as a plain textarea that autosaves
   * (`true`).
   */
  editModeOff?: boolean
  setHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void
  hideLabel?: boolean
}

export const ShortDescriptionEditor = ({
  setProjectShortDescription,
  onSaveStats,
  editModeOff = false,
  setHasUnsavedChanges,
  hideLabel,
}: ShortDescriptionEditorProps) => {
  const {hasWritePermissions} = ViewerPrivileges()
  const {shortDescription, shortDescriptionHtml} = useProjectDetails()
  const {updateShortDescription} = useUpdateMemexShortDescription()
  const [localShortDescription, setLocalShortDescription] = useState(shortDescription)
  const [isError, setIsError] = useState(false)
  const [isEdit, setIsEdit] = useState(editModeOff)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const ref = useRef<HTMLTextAreaElement | null>(null)
  const iconButtonWithTooltipRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setLocalShortDescription(shortDescription)
    if (setHasUnsavedChanges) setHasUnsavedChanges(false)
  }, [shortDescription, setHasUnsavedChanges])

  const onSave = useCallback(async () => {
    try {
      setIsDisabled(true)
      setIsError(false)
      onSaveStats?.(!!shortDescription)
      await updateShortDescription(localShortDescription)
      if (!editModeOff) setIsEdit(false)
      if (setHasUnsavedChanges) setHasUnsavedChanges(false)
      setIsSuccess(true)
      setTimeout(() => {
        setIsSuccess(false)
      }, TIMEOUT_SUCCESS_HIDE)
    } catch {
      setIsError(true)
    } finally {
      setIsDisabled(false)
    }
  }, [onSaveStats, shortDescription, editModeOff, setHasUnsavedChanges, localShortDescription, updateShortDescription])

  const onCancel = useCallback(() => {
    setLocalShortDescription(shortDescription)
    if (!editModeOff) setIsEdit(false)
    if (setHasUnsavedChanges) setHasUnsavedChanges(false)
  }, [setHasUnsavedChanges, shortDescription, editModeOff])

  const onEdit = useCallback(() => setIsEdit(true), [])

  useEffect(() => {
    if (isEdit && ref.current && !editModeOff) {
      ref.current.focus()
      ref.current.selectionStart = ref.current.selectionEnd = ref.current.value.length
    }
  }, [isEdit, editModeOff])

  const onChange = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      const singleLineValue = e.currentTarget.value.replace(/\s+/gs, ' ')
      if (setHasUnsavedChanges) setHasUnsavedChanges(singleLineValue !== shortDescription)
      setLocalShortDescription(singleLineValue)
      setProjectShortDescription?.(singleLineValue)
    },
    [setHasUnsavedChanges, shortDescription, setProjectShortDescription],
  )

  return (
    <>
      {!editModeOff && (
        <div className={styles.Box}>
          <div className={styles.Box_1}>
            <Octicon icon={NoteIcon} className={styles.Octicon} />
            <h3 className={styles.Text}>{DescriptionEditorResources.shortDescriptionTitle}</h3>
          </div>

          {!isEdit && hasWritePermissions && (
            <IconButton
              variant="invisible"
              onClick={onEdit}
              icon={PencilIcon}
              aria-label="Edit short description"
              ref={iconButtonWithTooltipRef}
              tooltipDirection="sw"
              className={styles.Octicon}
              {...testIdProps('description-editor-edit')}
            />
          )}
        </div>
      )}
      {isEdit ? (
        <>
          <FormControl>
            <FormControl.Label visuallyHidden={hideLabel}>
              {DescriptionEditorResources.shortDescriptionTitle}
            </FormControl.Label>
            <EmojiAutocomplete style={{width: '100%', maxWidth: editModeOff ? '600px' : undefined}}>
              <Textarea
                ref={ref}
                placeholder={DescriptionEditorResources.shortDescriptionPlaceholder}
                onChange={onChange}
                maxLength={MAX_LENGTH}
                value={localShortDescription}
                {...testIdProps('description-editor')}
              />
            </EmojiAutocomplete>
          </FormControl>

          {!setProjectShortDescription && (
            <div
              className={clsx(styles.Box_2, editModeOff ? styles.editModeOff : styles.editModeOn)}
              {...testIdProps('readme-editor-buttons')}
            >
              {isSuccess && editModeOff ? (
                <>
                  <span className={styles.Text_1}>Saved</span> <SuccessState />
                </>
              ) : localShortDescription !== shortDescription || !editModeOff ? (
                <>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={onSave}
                    disabled={isDisabled}
                    className={styles.Button}
                  >
                    {DescriptionEditorResources.saveButton}
                  </Button>
                  <Button
                    variant="invisible"
                    size="small"
                    onClick={onCancel}
                    disabled={isDisabled}
                    className={styles.Octicon}
                  >
                    {DescriptionEditorResources.cancelButton}
                  </Button>
                </>
              ) : null}
            </div>
          )}

          {isError && <ErrorState caret="top-left" message={DescriptionEditorResources.errorMessage} />}
        </>
      ) : (
        <div className={styles.Box_3}>
          {shortDescriptionHtml ? (
            <SanitizedHtml>{shortDescriptionHtml}</SanitizedHtml>
          ) : (
            <div className={styles.Box_4}>{DescriptionEditorResources.shortDescriptionPlaceholder}</div>
          )}
        </div>
      )}
    </>
  )
}
